/**
 * pwReset.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { Alert, Button, CircularProgress, FormControl, TextField } from "@mui/material";

import { authServiceFactory } from "../services/auth.service";

export function PasswordResetWrapper(): React.ReactElement<any, any> {
  const { token } = useParams();
  return <PasswordResetContainer token={token} />;
}

interface Props {
  token: string | undefined
}

interface State {
  sending: boolean,
  success: boolean,
  pass: string,
  confirmpass: string,
  errMsg: string,
  errors: {
    pass: string,
    confirmpass: string
  }
}

export class PasswordResetContainer extends React.Component<Props, State> {

  private confirmInput: HTMLInputElement | undefined;

  constructor(props: Props) {
    super(props);

    this.onBlur = this.onBlur.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.disablePassSubmit = this.disablePassSubmit.bind(this);
    this.changePass = this.changePass.bind(this);

    this.state = {
      sending: false,
      success: false,
      pass: "",
      confirmpass: "",
      errMsg: "",
      errors: {
        pass: "",
        confirmpass: ""
      }
    };
  }

  componentDidMount (): void {
    const token = this.props.token;
    if (token) {
      try {
        authServiceFactory().setAccessToken(token);
        console.log("have token:", token);
      } catch (err) {
        this.setState({errMsg: (err as Error).message});
      }
    } else {
      this.setState({errMsg: "Missing Token For Password Reset"});
    }
  }

  private onBlur(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    let msg = "";
    switch (propName) {
      case "pass":
        if (!this.state.pass) {
          msg = "Please, choose a new password";
        } else {
          msg = authServiceFactory().checkPWRequirements(this.state.pass);
        }
        break;
      case "confirmpass":
        if (!this.state.confirmpass) {
          msg = "Please, re-enter new password";
        } else if (this.state.confirmpass !== this.state.pass) {
          msg = "Password mismatch";
        }
        break;
      default:
        console.warn("Unknown input:", inputElem);
        return;
    }
    this.setState({errors: {...this.state.errors, [propName]: msg}});
  }

  private onKeyUp(event: React.KeyboardEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (event.key === "Enter") {
      switch (propName) {
        case "pass":
          this.confirmInput?.focus();
          break;
        default:
          return;
      }
    }
  }

  updateValue(event: React.SyntheticEvent): void {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = inputElem.value;
    const update: any = {[propName]: newValue};
    this.setState(update);

    let msg = "";
    if (propName === "pass") {
      msg = authServiceFactory().checkPWRequirements(newValue);
    } else if (propName === "confirmpass") {
      if (!newValue) {
        return;
      } else if (newValue !== this.state.pass) {
        msg = "Password mismatch";
      }
    }
    this.setState({errors: {...this.state.errors, [propName]: msg}});
  }

  private disablePassSubmit(): boolean {
    return !this.state.pass || !this.state.confirmpass ||
      this.state.confirmpass !== this.state.pass;
  }

  private async changePass() {

    if (!this.state.pass) {
      this.setState({errors: {...this.state.errors, pass: "Please, enter a new password"}});
      return;
    }
    if (!this.state.confirmpass) {
      this.setState({errors: {...this.state.errors, confirmpass: "Please, re-enter new password"}});
      return;
    }
    if (this.state.confirmpass !== this.state.pass) {
      return; // Error should already be displayed by onBlur
    }

    try {
      this.setState({sending: true});
      await authServiceFactory().changePassword("", this.state.pass);
      this.setState({
        pass: "",
        confirmpass: "",
        success: true,
        sending: false
      });
    } catch (err) {
      console.error("Error changing password:", err);
      this.setState({errMsg: (err as Error).message});
    } finally {
      this.setState({sending: false});
    }
  }

  render(): React.ReactElement<any, any> {
    return <div className="main-section-inner password-request-container">
      <header className="welcome-heading">
        <div className="logo-img" />
      </header>
      {this.state.success ? this.renderSuccess() : this.renderForm()}
    </div>;
  }

  renderForm(): React.ReactElement<any, any> {
    return <section className="password-reset-form">
      <form className="form-wrapper">
        { this.state.errMsg && <Alert
          variant="filled"
          severity="error">{this.state.errMsg}</Alert>}
        <header>
          <h2>Password Reset</h2>
          <p>Enter your new password.</p>
          <div className="password-rules">
            <p>Passwords must:</p>
            <ul>
              <li>be 10 characters or longer,</li>
              <li>contain one lower case letter,</li>
              <li>contain one capital letter,</li>
              <li>contain one number,</li>
              <li>contain one special character, or</li>
              <li>be 24 characters or longer</li>
            </ul>
          </div>
        </header>
        <div className="form-row">
            <FormControl fullWidth>
              <TextField id="pass-input" label="New Password" name="pass"
                type="password"
                required
                error={!!this.state.errors.pass}
                helperText={this.state.errors.pass}
                variant="outlined"
                value={this.state.pass}
                onChange={this.updateValue}
                onBlur={this.onBlur}
                onKeyUp={this.onKeyUp} />
            </FormControl>
          </div>
          <div className="form-row">
            <FormControl fullWidth>
              <TextField id="confirm-input" label="Confirm New Password" name="confirmpass"
                required
                type="password"
                error={!!this.state.errors.confirmpass}
                helperText={this.state.errors.confirmpass}
                variant="outlined"
                value={this.state.confirmpass}
                inputRef={input => { this.confirmInput = input; } }
                onChange={this.updateValue}
                onBlur={this.onBlur}
                onKeyUp={this.onKeyUp} />
            </FormControl>
          </div>
          <div className="button-row">
            <Button
                variant="contained"
                disableRipple disableTouchRipple
                onClick={this.changePass}
                disabled={this.disablePassSubmit()}>
              {this.state.sending ? <CircularProgress size={20} /> : "Change"}
            </Button>
          </div>
      </form>
    </section>;
  }

  renderSuccess(): React.ReactElement<any, any> {
    return <section className="password-success-msg">
      <h2>Success!</h2>
      <p>Your password has been reset! You should be able to log in now.</p>
      <Button variant="contained" component={Link} to="/login">Login</Button>
    </section>;
  }
}